"use client"

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { useState } from "react"
import ReactMarkdown from "react-markdown"

interface ModalSectionProps {
  title: string
  description: string
  ctaButton: {
    text: string
  }
  modal: {
    title: string
    content: string
  }
}

interface ModalSectionComponentProps {
  props: ModalSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
}

export function ModalSection({ props, styles }: ModalSectionComponentProps) {
  const { title, description, ctaButton, modal } = props
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <div
        style={{
          backgroundColor: styles?.backgroundColor,
          color: styles?.textColor,
          padding: styles?.padding,
          margin: styles?.margin,
        }}
        className="flex flex-col items-center justify-center py-16 text-center md:py-24"
      >
        {/* Title */}
        <h2 className="mb-6 max-w-4xl font-semibold text-5xl leading-tight md:mb-8 md:text-6xl">
          {title}
        </h2>

        {/* Description */}
        <p className="mb-8 max-w-5xl text-base leading-relaxed opacity-70 md:mb-12 md:text-lg">
          {description}
        </p>

        {/* CTA Button */}
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center rounded-full bg-foreground px-8 py-4 font-semibold text-background transition-all hover:scale-105 hover:opacity-90 active:scale-95"
        >
          {ctaButton.text}
        </button>
      </div>

      {/* Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-h-[85vh] overflow-y-auto bg-white text-black"
          style={{ fontFamily: "var(--brand-font-family)" }}
        >
          <DialogHeader>
            <DialogTitle className="text-left font-semibold text-xl">
              {modal.title}
            </DialogTitle>
          </DialogHeader>

          {/* Modal content with markdown support */}
          <div className="prose prose-sm mt-4 max-w-none text-black">
            <ReactMarkdown
              components={{
                h1: ({ children }) => (
                  <h1 className="mb-4 font-semibold text-2xl">{children}</h1>
                ),
                h2: ({ children }) => (
                  <h2 className="mt-6 mb-3 font-semibold text-xl">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="mt-4 mb-3 font-medium text-lg">{children}</h3>
                ),
                p: ({ children }) => (
                  <p className="mb-4 text-black leading-relaxed">{children}</p>
                ),
                ul: ({ children }) => (
                  <ul className="mb-4 ml-6 list-disc space-y-2">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-4 ml-6 list-decimal space-y-2">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="text-black leading-relaxed">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold">{children}</strong>
                ),
                em: ({ children }) => <em className="italic">{children}</em>,
                blockquote: ({ children }) => (
                  <blockquote className="my-4 rounded-lg border-gray-300 border-l-4 bg-gray-50 py-3 pr-4 pl-4 italic">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm">
                    {children}
                  </code>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline hover:text-blue-800"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {modal.content}
            </ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
